// In-portal Help — renders as a modal overlay on top of the prototype list,
// not a separate route. The hash routing (#help) drives open/close so the
// URL stays shareable and back-button works.

export interface PortalConfig {
  apiUrl: string;
  // OAuth values for the rewind-protos skill's CLI client. Populated from
  // SSM at deploy time. The skill reads these on every login (instead of
  // bundling them into the zip).
  googleCliClientId: string;
  googleCliClientSecret: string;
  skillVersion: string;
  skillUrl: string;
  uploadLimits: {
    maxZipBytes: number;
    maxExtractedBytes: number;
    maxEntryBytes: number;
    maxEntries: number;
    maxPathDepth: number;
    allowedExtensions: string[];
  };
}

function fmtMB(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

// Escape strings interpolated into innerHTML. /config.json is server-controlled
// so values are trusted today, but defense-in-depth in case future config sources
// (URL params, user-provided overrides, etc.) ever feed into renderHelp.
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function renderHelp(root: HTMLElement, cfg: PortalConfig): void {
  // Use the stable /skill-latest.zip alias so users (and anyone copy-pasting
  // this command into a Slack message or Confluence) don't end up with a URL
  // that 404s on the next version bump. The "Current skill version" line below
  // still shows the version explicitly for transparency.
  const portalOrigin = window.location.origin;
  const installUrl = `${portalOrigin}/skill-latest.zip`;
  const apiUrlSafe = esc(cfg.apiUrl);
  const skillVersionSafe = esc(cfg.skillVersion);
  const allowedExtsSafe = esc(cfg.uploadLimits.allowedExtensions.join(", "));

  // The install one-liner below downloads to /tmp first, then nukes the old
  // install dir before extracting. This ordering matters:
  // 1. Download-first means a network failure leaves the old install intact
  //    (not a missing skill dir).
  // 2. The nuke-before-extract ensures `unzip -o` starts clean — without it,
  //    files dropped between versions (e.g. v0.1.x's lib/oauth-config.json)
  //    linger as orphans because unzip only overwrites, never deletes.
  // Safe because credentials live in ~/.config/rewind-protos/, not the skill dir.
  root.innerHTML = `
    <section class="card help-view">
      <h2>Help</h2>

      <h3>Upload via the web</h3>
      <ul>
        <li>Click <strong>Upload</strong>, give it a title, choose your ZIP.</li>
        <li>Limits: ${fmtMB(cfg.uploadLimits.maxZipBytes)} zip,
          ${fmtMB(cfg.uploadLimits.maxExtractedBytes)} extracted,
          ${Number(cfg.uploadLimits.maxEntries)} files max,
          ${Number(cfg.uploadLimits.maxPathDepth)} levels deep,
          ${fmtMB(cfg.uploadLimits.maxEntryBytes)} per file.</li>
        <li>Allowed file types: ${allowedExtsSafe}</li>
      </ul>

      <h3>Use Claude Code (recommended)</h3>

      <h4>1. Install / upgrade</h4>
      <p>Paste in a terminal:</p>
      <pre><code>curl -fsSL ${installUrl} -o /tmp/rewind-protos-skill.zip &amp;&amp; \\
  rm -rf ~/.claude/skills/rewind-protos &amp;&amp; \\
  mkdir -p ~/.claude/skills/rewind-protos &amp;&amp; cd ~/.claude/skills/rewind-protos &amp;&amp; \\
  unzip -o /tmp/rewind-protos-skill.zip &amp;&amp; rm /tmp/rewind-protos-skill.zip</code></pre>

      <h4>2. Log in (one-time per machine)</h4>
      <p>In Claude Code, ask: <em>"log in to the prototype portal"</em>. A browser opens, sign in with your <code>@rewind.io</code> account. Refresh token is stored at <code>~/.config/rewind-protos/credentials.json</code> (mode 0600).</p>

      <h4>3. Sample commands you can ask Claude</h4>
      <ul>
        <li><em>"list my prototypes"</em> — lists prototypes you own with their viewer URLs</li>
        <li><em>"list all prototypes"</em> — same but includes everyone's, with owner column</li>
        <li><em>"push the folder at ./hot-standby as a new prototype titled 'Hot Standby'"</em> — creates a brand-new prototype</li>
        <li><em>"push that update"</em> — pushes a new version of the same folder; slug is auto-detected from <code>./hot-standby/.rewind-protos.json</code></li>
        <li><em>"push that update with note 'fixed the nav layout'"</em> — same, with a custom version note (default is <em>"Updated via Claude skill"</em>)</li>
        <li><em>"log out of the prototype portal"</em> — clears stored credentials</li>
        <li><em>"upgrade rewind-protos"</em> — re-installs the latest version (also see "Updating" below)</li>
      </ul>
      <p>Works with the <code>/prototype</code> skill — that skill calls <code>publish.mjs</code> automatically when this one is installed, so you can use either entry point.</p>

      <h4>4. Updating the skill</h4>
      <p>The skill checks its bundled version against the deployed version on every command. When out of date, it prints a one-line warning to stderr that Claude surfaces back to you, like:</p>
      <pre><code>⚠ rewind-protos vX.Y.Z is outdated (latest: ${skillVersionSafe}). Update: ${portalOrigin}/#help</code></pre>
      <p>To upgrade, ask Claude <em>"upgrade rewind-protos"</em> — Claude will re-run the install one-liner above. Your existing credentials carry over (no need to re-login). The same one-liner installs and upgrades because <code>skill-latest.zip</code> always points at the current build.</p>

      <p><small>Current deployed skill version: <strong>${skillVersionSafe}</strong></small></p>

      <h3>Programmatic / CI (per-prototype token)</h3>
      <p>For non-Claude automation: click <strong>Generate Upload Token</strong> on any prototype you own (8h TTL, scoped to that prototype only).</p>
      <details>
        <summary>Show curl example</summary>
        <pre><code># 1. Create a new version (returns presigned upload URL)
curl -X POST ${apiUrlSafe}/prototypes/SLUG/versions \\
  -H "Authorization: ProtoToken YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"note":"Updated nav","uploadedBy":"ci-job-42"}'

# 2. Upload the ZIP to the returned uploadUrl using the returned fields
# (multipart/form-data; see response.fields for the exact field set)</code></pre>
      </details>
    </section>
  `;
}

// ---- Modal lifecycle ----

const MODAL_ID = "help-modal";
// Element to restore focus to when the modal closes. Saved on open.
let previouslyFocused: Element | null = null;

/**
 * Mount the help modal as an overlay on top of the existing page.
 * Idempotent — calling twice does nothing.
 *
 * Close behavior: click overlay backdrop, click × button, or press Escape.
 * Each closure removes `#help` from the URL hash (via replaceState — see
 * clearHelpHash) so the back button doesn't get a "reopen modal" entry.
 */
export function openHelpModal(cfg: PortalConfig): void {
  if (document.getElementById(MODAL_ID)) return; // already open

  // Save the element that had focus before the modal opened so we can
  // restore it on close (a11y requirement for modal dialogs).
  previouslyFocused = document.activeElement;

  const overlay = document.createElement("div");
  overlay.id = MODAL_ID;
  overlay.className = "modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Help");

  const dialog = document.createElement("div");
  dialog.className = "modal";
  // Stop overlay click from bubbling — we close on overlay click but NOT on
  // dialog click (so users can interact with the dialog content).
  dialog.addEventListener("click", (e) => e.stopPropagation());

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "modal-close";
  closeBtn.setAttribute("aria-label", "Close help");
  closeBtn.innerHTML = "&times;";
  closeBtn.addEventListener("click", clearHelpHash);

  const content = document.createElement("div");
  content.className = "modal-content";
  renderHelp(content, cfg);

  dialog.append(closeBtn, content);
  overlay.append(dialog);
  overlay.addEventListener("click", clearHelpHash);
  document.addEventListener("keydown", onKeydown);
  document.body.append(overlay);
  document.body.classList.add("modal-open");

  // Focus the close button so Escape / Enter just work and screen readers
  // announce the dialog.
  closeBtn.focus();
}

export function closeHelpModal(): void {
  const overlay = document.getElementById(MODAL_ID);
  if (!overlay) return;
  document.removeEventListener("keydown", onKeydown);
  overlay.remove();
  document.body.classList.remove("modal-open");

  // Restore focus to whatever had it before we opened.
  if (previouslyFocused instanceof HTMLElement) {
    previouslyFocused.focus();
  }
  previouslyFocused = null;
}

// Keyboard handlers while the modal is open: Escape closes; Tab/Shift+Tab
// cycle within the dialog (focus trap so tabbing can't reach content
// behind the overlay — a11y requirement for modal dialogs).
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    clearHelpHash();
    return;
  }
  if (e.key !== "Tab") return;
  const overlay = document.getElementById(MODAL_ID);
  if (!overlay) return;
  const focusables = overlay.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea, summary, details',
  );
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement as HTMLElement | null;
  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

function clearHelpHash() {
  // Use replaceState (not pushState) so closing the modal doesn't add a
  // history entry — otherwise pressing Back after closing would navigate
  // back to #help and reopen the modal (opposite of the intended UX).
  // We dispatch hashchange manually because replaceState doesn't fire it.
  if (location.hash === "#help") {
    history.replaceState("", document.title, location.pathname + location.search);
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  } else {
    closeHelpModal();
  }
}
